import type { OrigemDaDemanda, OrigemDoChamado, SituacaoDeSla } from '../types/chamado'

/**
 * Os critérios da listagem de chamados, como a tela os enxerga.
 *
 * **Competência e período são eixos diferentes, e a diferença é a que decide denominador.** A
 * competência é o mês de FECHAMENTO — decisão do dono, e o que o TRP assinado de maio/2026 usou:
 * dos chamados da Peça 101, 70 foram *abertos* no período e 45 *fecharam* nele, e os indicadores do
 * documento (ICA 3/45, IAP 25/36, IAE 45/45) são todos sobre os 45. `de` e `ate` filtram por
 * ABERTURA, que é o recorte do contrato ("os chamados desde 06/05/2026"). Os nomes dizem o que cada
 * um faz para ninguém precisar adivinhar — e é a mesma nomenclatura de `FiltroChamadoRequest`.
 */
export interface FiltrosDeChamado {
  /** Mês de FECHAMENTO, no formato `AAAA-MM`. Vazio traz todos, inclusive os ainda abertos. */
  competencia: string
  /** Data inicial de ABERTURA, inclusive (aaaa-mm-dd). */
  de: string
  /** Data final de ABERTURA, inclusive (aaaa-mm-dd). */
  ate: string
  grupo: string
  entidade: string
  situacaoSla: SituacaoDeSla[]
  origemDemanda: OrigemDaDemanda[]
  origem: OrigemDoChamado[]
  q: string
  /**
   * Código de alerta apontado pela análise automática.
   *
   * É o que tira o achado de dentro de um chamado só: o parecer levantava "Pendente usado para
   * parar o relógio" — o principal vetor conhecido de burla — e isso morria na tela daquele
   * chamado.
   */
  alertaDaIa: string
  /**
   * O chamado já tem julgamento do fiscal? `null` traz os dois.
   *
   * **É o que transforma 107 chamados numa fila de trabalho.** Agosto/2026 fecha com 107 chamados e
   * ZERO marcações, e o fechamento recusa enquanto sobrar um. Sem este recorte, saber o que já foi
   * julgado exigia abrir chamado por chamado.
   *
   * Tri-estado, e não booleano: `false` é a fila, `true` é a conferência do que já foi feito, e
   * `null` é a lista de sempre. Com dois estados, "todos" deixaria de existir.
   */
  julgado: boolean | null
  /** Página que o usuário vê, começando em 1. A API conta a partir de zero. */
  pagina: number
  tamanho: number
}

export const TAMANHO_PADRAO = 20

/**
 * A listagem de chamados NÃO tem procedência padrão, ao contrário da de GMUD.
 *
 * Lá o padrão é `['V2']`, porque as 1.027 solicitações importadas tomariam a primeira página e se
 * leriam como aprovações do comitê. Aqui esconder uma procedência esconderia o único dado que
 * existe: hoje tudo vem da planilha da fiscalização, e o adaptador do e-Niterói ainda não foi
 * escrito. O que separa as duas fontes é a marca em cada linha e o carimbo no topo da tela, não um
 * recorte silencioso — e é assim que tem de continuar quando a segunda fonte chegar, porque as duas
 * discordam por construção e some-las sem dizer qual é qual produz uma terceira realidade.
 */
export const SEM_FILTRO: FiltrosDeChamado = {
  competencia: '',
  de: '',
  ate: '',
  grupo: '',
  entidade: '',
  situacaoSla: [],
  origemDemanda: [],
  origem: [],
  q: '',
  alertaDaIa: '',
  julgado: null,
  pagina: 1,
  tamanho: TAMANHO_PADRAO,
}

/**
 * A tradução entre a query string da tela e a da API, num lugar só.
 *
 * São dois dialetos parecidos e diferentes: a URL do navegador conta páginas a partir de 1, porque
 * é o que aparece escrito para o usuário; a API conta a partir de zero. Ter as duas conversões lado
 * a lado é o que impede o clássico "a página 2 mostra a 3".
 *
 * @param parametros a query string do navegador
 * @returns os critérios correspondentes
 */
export function lerDaUrl(parametros: URLSearchParams): FiltrosDeChamado {
  return {
    competencia: texto(parametros.get('competencia')),
    de: texto(parametros.get('de')),
    ate: texto(parametros.get('ate')),
    grupo: texto(parametros.get('grupo')),
    entidade: texto(parametros.get('entidade')),
    situacaoSla: parametros.getAll('situacaoSla') as SituacaoDeSla[],
    origemDemanda: parametros.getAll('origemDemanda') as OrigemDaDemanda[],
    origem: parametros.getAll('origem') as OrigemDoChamado[],
    q: texto(parametros.get('q')),
    alertaDaIa: texto(parametros.get('alertaDaIa')),
    julgado: booleano(parametros.get('julgado')),
    pagina: inteiro(parametros.get('pagina'), 1),
    tamanho: inteiro(parametros.get('tamanho'), TAMANHO_PADRAO),
  }
}

/**
 * Escreve só o que difere do padrão.
 *
 * Um link com `?competencia=2026-05` é legível e sobrevive a uma mudança de padrão; um com
 * `?competencia=&de=&ate=&grupo=&pagina=1&tamanho=20` não é nenhuma das duas coisas. E `/chamados`
 * continua sendo o endereço limpo da listagem, que `lerDaUrl` relê idêntico — ida e volta estável.
 *
 * @param filtros critérios a codificar
 * @returns a query string correspondente
 */
export function escreverNaUrl(filtros: FiltrosDeChamado): URLSearchParams {
  const parametros = new URLSearchParams()
  escreverTexto(parametros, 'competencia', filtros.competencia)
  escreverTexto(parametros, 'de', filtros.de)
  escreverTexto(parametros, 'ate', filtros.ate)
  escreverTexto(parametros, 'grupo', filtros.grupo)
  escreverTexto(parametros, 'entidade', filtros.entidade)
  filtros.situacaoSla.forEach((s) => parametros.append('situacaoSla', s))
  filtros.origemDemanda.forEach((o) => parametros.append('origemDemanda', o))
  filtros.origem.forEach((o) => parametros.append('origem', o))
  escreverTexto(parametros, 'q', filtros.q)
  escreverTexto(parametros, 'alertaDaIa', filtros.alertaDaIa)
  if (filtros.julgado !== null) {
    parametros.set('julgado', String(filtros.julgado))
  }
  if (filtros.pagina > 1) {
    parametros.set('pagina', String(filtros.pagina))
  }
  if (filtros.tamanho !== TAMANHO_PADRAO) {
    parametros.set('tamanho', String(filtros.tamanho))
  }
  return parametros
}

/**
 * Os mesmos critérios no formato que `GET /chamados` espera.
 *
 * As listas vão como parâmetros repetidos (`situacaoSla=A&situacaoSla=B`), que é o que o Spring liga
 * a uma `List<>`. O serializador padrão do axios escreveria `situacaoSla[]=A`, que chega como filtro
 * vazio — e filtro vazio significa "todos", ou seja, a tela mostraria mais registros do que o
 * usuário pediu, sem nenhum erro aparente.
 *
 * @param filtros critérios da tela
 * @returns a query string da consulta
 */
export function paraConsulta(filtros: FiltrosDeChamado): URLSearchParams {
  const parametros = escreverNaUrl(filtros)
  parametros.set('pagina', String(Math.max(filtros.pagina - 1, 0)))
  parametros.set('tamanho', String(filtros.tamanho))
  return parametros
}

function escreverTexto(parametros: URLSearchParams, chave: string, valor: string): void {
  if (valor.trim()) {
    parametros.set(chave, valor.trim())
  }
}

function texto(valor: string | null): string {
  return valor?.trim() ?? ''
}

/**
 * `julgado` na URL: só `true` e `false` valem; qualquer outra coisa é "todos".
 *
 * Um valor inválido vira `null` — e não um dos dois lados — porque um link torto não pode esconder
 * metade da fila de trabalho sem dizer que a escondeu.
 */
function booleano(valor: string | null): boolean | null {
  if (valor === 'true') {
    return true
  }
  if (valor === 'false') {
    return false
  }
  return null
}

function inteiro(valor: string | null, padrao: number): number {
  const numero = Number(valor)
  return Number.isInteger(numero) && numero > 0 ? numero : padrao
}
